import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../../../utils/auth";
import { Plus, Eye, Calendar, Users } from "lucide-react";
import { assignmentsBase } from "./assignmentRules";
import apiClient from "../../../utils/apiClient";
import {
  AppButton, StatusBadge, IconButton, FilterToolbar, AppDataTable, sortRows,
} from "../../ui";

/**
 * BatchGeneralAssignments — general assignments for one batch, embedded in
 * `Batches/BatchDetails/BatchDetailsPage` as a tab. "New Assignment" opens the
 * form with this batch already chosen.
 *
 * Sorting is on and filters are not: this is one batch's assignments inside a
 * tab, so it is a short list someone reads rather than interrogates. The
 * institute-wide list (`GeneralAssignmentList`) is where the full set belongs.
 */

const DASH = <span className="tw-text-content-subtle">—</span>;

const routePrefix = () => assignmentsBase(getCurrentUser()?.usertype);

const BatchGeneralAssignments = ({ batchId }) => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(null);

  useEffect(() => {
    if (!batchId) return;
    apiClient.get("/general-assignments/list", { params: { batchId } })
      .then(({ data }) => setAssignments(data.assignments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batchId]);

  const columns = useMemo(() => [
    {
      key: "title",
      label: "Title",
      sortable: true,
      sortValue: (a) => a.title || "",
      render: (a) => <span className="tw-font-medium tw-text-content">{a.title}</span>,
    },
    {
      key: "dueDate",
      label: "Due Date",
      sortable: true,
      sortValue: (a) => a.dueDate || null,
      render: (a) => (a.dueDate ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <Calendar size={13} />
          {/* ⚠️ IST explicitly. The box is UTC and a bare toLocaleDateString
              names the previous day for anything due after 05:30 IST. */}
          {new Date(a.dueDate).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
          })}
        </span>
      ) : DASH),
    },
    {
      key: "maxMarks",
      label: "Max Marks",
      align: "right",
      sortable: true,
      sortValue: (a) => a.maxMarks ?? 0,
      render: (a) => <span className="tw-tabular-nums">{a.maxMarks}</span>,
    },
    {
      key: "submissionCount",
      label: "Submissions",
      align: "right",
      sortable: true,
      sortValue: (a) => a.submissionCount ?? 0,
      render: (a) => (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums">
          <Users size={13} /> {a.submissionCount || 0}
          {a.toGradeCount > 0 && (
            <StatusBadge status="warning">{a.toGradeCount} to grade</StatusBadge>
          )}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortValue: (a) => a.status || "",
      render: (a) => (
        <StatusBadge status={a.status} domain="assignment" />
      ),
    },
    {
      key: "actions",
      label: "",
      width: 56,
      pinned: "right",
      resizable: false,
      // ⚠️ `IconButton`, not a bare icon `<button>` with a `title` — a tooltip
      // is not an accessible name, and this column held one per row.
      //
      // The row is clickable, so the button must stop the click reaching it.
      render: (a) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          {/* `icon={Eye}`, not a child — the size prop then owns the glyph size. */}
          <IconButton
            size="sm"
            label={`View ${a.title}`}
            icon={Eye}
            onClick={() => navigate(`${routePrefix()}/${a._id}`)}
          />
        </div>
      ),
    },
  ], [navigate]);

  const rows = useMemo(() => sortRows(assignments, columns, sort), [assignments, columns, sort]);

  return (
    <AppDataTable
      fill={false}
      toolbar={
        <FilterToolbar
          flush
          actions={
            <AppButton size="sm" onClick={() => navigate(`${routePrefix()}/create?batchId=${batchId}`)}>
              <Plus size={16} /> New Assignment
            </AppButton>
          }
        >
          <span className="tw-text-[0.8125rem] tw-text-content-subtle">
            {assignments.length} assignment{assignments.length === 1 ? "" : "s"}
          </span>
        </FilterToolbar>
      }
      columns={columns}
      rows={rows}
      rowKey={(a) => a._id}
      loading={loading}
      sort={sort}
      onSortChange={(key, direction) => setSort({ key, direction })}
      onRowClick={(a) => navigate(`${routePrefix()}/${a._id}`)}
      emptyMessage="No assignments yet. Create a general assignment for this batch."
    />
  );
};

export default BatchGeneralAssignments;
